import React, { useEffect, useMemo, useState } from 'react';
import { useCurrentGameweek } from '../../hooks/useCurrentGameweek';
import { useTeamContext } from '../../contexts/TeamContext';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Flame, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

type PointsHitRow = {
  team: string;
  manager: string;
  teamId: string;
  pointsHit: number;
};

async function fetchTransfersForTeam(gameweek: string, teamId: number) {
  const API_BASE_URL = process.env.REACT_APP_API_URL as string;
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/team/${teamId}/transfers/${gameweek}`
    );
    return response.data.transfersCost ?? 0;
  } catch {
    return 0;
  }
}

const PointsHit: React.FC = () => {
  const navigate = useNavigate();
  const { teams } = useTeamContext();
  const { currentGameweek, selectedGameweek, setSelectedGameweek } = useCurrentGameweek();

  const location = useLocation();
  const state = location.state as { gameweek?: number } | null;
  const gameweekToUse = state?.gameweek ?? selectedGameweek ?? currentGameweek;

  const [loading, setLoading] = useState<boolean>(true);
  const [pointsHitData, setPointsHitData] = useState<PointsHitRow[]>([]);

  useEffect(() => {
    if (state?.gameweek !== undefined && setSelectedGameweek) {
      setSelectedGameweek(state.gameweek);
    }
  }, [state?.gameweek, setSelectedGameweek]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!gameweekToUse || teams.length === 0) {
        setPointsHitData([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const results = await Promise.all(
        teams.map(async (team) => {
          const pointsHit = await fetchTransfersForTeam(String(gameweekToUse), Number(team.teamId));
          return {
            team: team.playerName,
            manager: team.playerName,
            teamId: team.teamId,
            pointsHit,
          } as PointsHitRow;
        })
      );
      if (!cancelled) {
        setPointsHitData(results.filter((r) => r.pointsHit > 0));
        setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [gameweekToUse, teams]);

  const sortedRows = useMemo(
    () => [...pointsHitData].sort((a, b) => b.pointsHit - a.pointsHit || a.team.localeCompare(b.team)),
    [pointsHitData]
  );

  const totalManagers = sortedRows.length;
  const totalPoints = sortedRows.reduce((acc, r) => acc + r.pointsHit, 0);
  const avgPoints = totalManagers ? Math.round((totalPoints / totalManagers) * 10) / 10 : 0;

  return (
    <div className="mx-4 text-content">
      <div className="flex items-center justify-between py-3">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex min-h-11 items-center gap-2 px-2 text-sm underline text-content-secondary hover:text-content transition-colors"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="text-sm">
          <span className="px-3 py-1 rounded-full bg-surface border border-line">
            GW {gameweekToUse ?? '—'}
          </span>
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-3xl font-extrabold tracking-tight">Managers Who Took a Points Hit</h2>
        <p className="text-sm text-content-secondary mt-2">
          Sorted by biggest hits first. Only managers with hits are shown.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-surface text-content rounded-2xl shadow ring-1 ring-line p-5 text-center">
          <div className="text-xs uppercase tracking-wide text-content-secondary">Managers with Hits</div>
          <div className="mt-1 text-xl md:text-3xl font-extrabold">{loading ? '—' : totalManagers}</div>
        </div>
        <div className="bg-surface text-content rounded-2xl shadow ring-1 ring-line p-5 text-center">
          <div className="text-xs uppercase tracking-wide text-content-secondary">Total Points Burned</div>
          <div className="mt-1 text-xl md:text-3xl font-extrabold">-{loading ? '—' : totalPoints} pts</div>
        </div>
        <div className="bg-surface text-content rounded-2xl shadow ring-1 ring-line p-5 text-center">
          <div className="text-xs uppercase tracking-wide text-content-secondary">Average per Manager</div>
          <div className="mt-1 text-xl md:text-3xl font-extrabold">-{loading ? '—' : avgPoints} pts</div>
        </div>
      </div>

      <div className="mt-6 mb-6">
        {loading && (
          <div className="grid gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-surface rounded-xl shadow ring-1 ring-line p-4 flex items-center justify-between overflow-hidden"
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="w-10 h-10 rounded-full bg-surface-sunken animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 bg-surface-sunken rounded w-2/5 animate-pulse" />
                    <div className="h-3 bg-surface-sunken rounded w-1/3 mt-2 animate-pulse" />
                  </div>
                </div>
                <div className="w-20 h-7 rounded-full bg-surface-sunken animate-pulse" />
              </div>
                   
                 
            ))}
          </div>
        )}

        {!loading && totalManagers === 0 && (
          <div className="bg-surface text-content rounded-2xl shadow ring-1 ring-line p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-green-100 dark:bg-green-500/15 flex items-center justify-center">
              <Zap size={22} className="text-green-700 dark:text-green-400" />
            </div>
            <h3 className="mt-3 text-xl font-semibold">No Hits This Week</h3>
            <p className="text-sm text-content-secondary mt-1">Everyone kept their free transfers.</p>
          </div>
        )}

        {/* List */}
        {!loading && totalManagers > 0 && (
          <div className="grid gap-3">
            {sortedRows.map((r, idx) => {
              const initials = r.manager
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((s) => s[0]?.toUpperCase())
                .join('');

              const isTop = idx === 0;

              return (
                  <Link to="/transfers" key={`${r.teamId}-${idx}`}  state={{ teamId: r.teamId ,gameweek: gameweekToUse }}>
                <div
                  className="group bg-surface text-content rounded-xl shadow ring-1 ring-line p-4 flex items-center justify-between hover:shadow-lg hover:ring-line-strong transition"
                >
                
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-inverse text-inverse-content flex items-center justify-center font-semibold">
                        {initials || 'M'}
                      </div>
                      {isTop && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                          <Flame size={11} className="text-red-600 dark:text-red-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col min-w-0">
                      <div className="font-semibold leading-tight truncate">{r.manager}</div>
                      <div className="text-xs text-content-secondary leading-tight truncate max-w-[260px]">
                        Team ID: {r.teamId}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className="inline-block px-3 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/25 dark:text-red-300 font-semibold group-hover:-translate-y-px transition-transform">
                      -{r.pointsHit} pts
                    </span>
                    <div className="text-2xs text-content-muted mt-1">Rank: {idx + 1}</div>
                  </div>
                
                </div>
                  </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PointsHit;
